// External packages
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

// Types
import type { Exhibit } from '../../../server/src/types/exhibit.types';

// Schemas
import { exhibitsCreateSchema } from 'schemas/exhibit';

// Hooks
import { useToast } from 'hooks/useToast';
import { API_BASE_URL, authHeaders } from './base';

export interface ExhibitResponse {
  error: boolean;
  data: Exhibit[];
}

export async function getExhibits(): Promise<Exhibit[]> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/exhibits`,
      { headers: authHeaders() }
    );

    if (!response.ok) throw new Error('Failed to fetch exhibits');

    const result: ExhibitResponse = await response.json();

    if (result.error) throw new Error('Failed to fetch exhibits');

    return result.data;
  } catch (error: any) {
    throw new Error(error.message || 'Network error during fetching exhibits');
  }
}

export const useGetExhibits = () => {
  return useQuery({
    queryKey: ['exhibits'],
    queryFn: getExhibits,
    initialData: [],
  });
};

export const createExhibit = async (
  data: z.infer<typeof exhibitsCreateSchema>,
  authToken: string = localStorage.getItem('authToken') || ''
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/exhibits`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok)
      throw new Error(`Failed to create exhibit: ${response.statusText}`);

    return response.json();
  } catch (error: any) {
    throw new Error(error.message || 'Network error during exhibit creation');
  }
};

export const useCreateExhibit = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ data }: { data: z.infer<typeof exhibitsCreateSchema> }) => {
      return createExhibit(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exhibits'] });
      toast.success('Eksponat je uspješno kreiran!');
    },
    onError: (error: any) => {
      console.error('Failed to create exhibit:', error);
      toast.error(error?.message || 'Greška pri kreiranju eksponata.');
    },
  });
};

export const deleteExhibit = async (id: string): Promise<void> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/exhibits/${id}`,
      {
        method: 'DELETE',
        headers: authHeaders(),
      }
    );

    if (!response.ok) throw new Error('Failed to delete exhibit');
  } catch (error: any) {
    throw new Error(error.message || 'Network error during exhibit deletion');
  }
};

export const useDeleteExhibit = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => {
      return deleteExhibit(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exhibits'] });
      toast.success('Eksponat je uspješno obrisan!');
    },
    onError: (error: any) => {
      console.error('Failed to delete exhibit:', error);
      toast.error(error?.message || 'Greška pri brisanju eksponata.');
    },
  });
};

export const updateExhibit = async (
  id: string,
  data: z.infer<typeof exhibitsCreateSchema>,
  authToken: string = localStorage.getItem('authToken') || ''
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/exhibits/${id}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok)
      throw new Error(`Failed to update exhibit: ${response.statusText}`);

    return response.json();
  } catch (error: any) {
    throw new Error(error.message || 'Network error during exhibit update');
  }
};

export const useUpdateExhibit = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: z.infer<typeof exhibitsCreateSchema>;
    }) => {
      return updateExhibit(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exhibits'] });
      toast.success('Eksponat je uspješno ažuriran!');
    },
    onError: (error: any) => {
      console.error('Failed to update exhibit:', error);
      toast.error(error?.message || 'Greška pri ažuriranju eksponata.');
    },
  });
};
